
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center">
          <img 
            src="https://public.readdy.ai/ai/img_res/c0eb2c6e-630d-4820-b716-1f4ac30bfc00.png" 
            alt="FORT SELECT LTDA Logo" 
            className="h-12 w-auto mr-4"
          />
          <h1 className="text-xl font-semibold text-gray-900 tracking-wide">
            FORT SELECT LTDA
          </h1>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 pb-32 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-7xl font-serif font-bold text-gray-900 leading-tight mb-6">
            Modern iOS Solutions
            <br />
            for Enhanced Security
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Expert team developing VPN and Cleaner applications for iOS platform
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-wider text-teal-700 mb-4 font-semibold">
                ABOUT US
              </p>
              <h3 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Expertise in iOS Development
              </h3>
              <p className="text-gray-600 text-base leading-relaxed mb-4">
                FORT SELECT LTDA is a dedicated team of experts specializing in developing modern applications for the iOS platform. We focus on creating innovative solutions that enhance user experience and improve the security of mobile devices.
              </p>
              <p className="text-gray-600 text-base leading-relaxed">
                Our commitment to excellence drives us to deliver high-quality products that meet the evolving needs of iOS users worldwide. With a deep understanding of mobile security and performance optimization, we craft applications that users can trust and rely on daily.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/1fb3f197c85aed8b73cd6c6099c3140e.jpg"
                alt="iOS Development Illustration"
                className="w-full h-auto rounded-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-wider text-teal-700 mb-4 font-semibold">
              OUR PRODUCTS
            </p>
            <h3 className="text-6xl font-serif font-bold text-gray-900">
              Innovative Applications
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* VPN Product Card */}
            <div className="relative h-[480px] rounded-2xl overflow-hidden shadow-lg group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20network%20visualization%20with%20glowing%20connection%20nodes%20and%20data%20streams%2C%20deep%20navy%20blue%20gradient%20background%2C%20cybersecurity%20concept%20art%2C%20digital%20network%20mesh%20pattern%2C%20secure%20VPN%20technology%20illustration%2C%20modern%20tech%20aesthetic%20with%20geometric%20lines%20and%20dots&width=400&height=480&seq=vpn-product-card-001&orientation=portrait"
                alt="VPN Product"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h4 className="text-4xl font-bold text-white mb-2">VPN</h4>
                <p className="text-white/80 text-base">Secure Connection</p>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="relative h-[480px] rounded-2xl overflow-hidden shadow-lg group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=minimalist%20storage%20optimization%20visualization%20with%20clean%20geometric%20shapes%2C%20soft%20mint%20green%20gradient%20background%2C%20digital%20cleaning%20concept%20with%20organized%20data%20blocks%2C%20performance%20optimization%20illustration%2C%20modern%20tech%20design%20with%20abstract%20storage%20icons%20and%20progress%20indicators&width=400&height=480&seq=cleaner-product-card-001&orientation=portrait"
                alt="Cleaner Product"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-600/90 via-emerald-600/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h4 className="text-4xl font-bold text-white mb-2">Cleaner</h4>
                <p className="text-white/80 text-base">Optimize Performance</p>
              </div>
            </div>

            {/* Combined Features Card */}
            <div className="relative h-[480px] rounded-2xl overflow-hidden shadow-lg group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20technology%20pattern%20with%20layered%20security%20shields%20and%20optimization%20symbols%2C%20light%20purple%20gradient%20background%2C%20all-in-one%20solution%20concept%20art%2C%20modern%20digital%20protection%20illustration%20with%20geometric%20tech%20elements%2C%20comprehensive%20security%20and%20performance%20visualization&width=400&height=480&seq=combined-features-card-001&orientation=portrait"
                alt="Complete Protection"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-700/90 via-purple-700/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h4 className="text-4xl font-bold text-white mb-2">Complete Protection</h4>
                <p className="text-white/80 text-base">All-in-One Solution</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-amber-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h3 className="text-6xl font-serif font-bold text-gray-900">
              Key Features
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
            {/* Feature 1 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
                <i className="ri-shield-check-line text-3xl text-blue-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Enhanced Privacy
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Advanced encryption protocols to protect your data and ensure complete privacy while browsing
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                <i className="ri-speed-up-line text-3xl text-emerald-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Device Optimization
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Intelligent cleaning algorithms that free up space and boost your device performance instantly
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-rose-100 flex items-center justify-center">
                <i className="ri-smartphone-line text-3xl text-rose-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                User-Friendly Interface
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Intuitive design that makes complex security features accessible to everyone with ease
              </p>
            </div>

            {/* Feature 4 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-100 flex items-center justify-center">
                <i className="ri-refresh-line text-3xl text-purple-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Regular Updates
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Continuous improvements and new features to keep your apps running at peak performance
              </p>
            </div>

            {/* Feature 5 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                24/7 Support
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Dedicated support team available around the clock to assist you with any questions
              </p>
            </div>

            {/* Feature 6 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-loop-left-line text-3xl text-amber-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Cross-Device Sync
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Seamlessly sync your settings and preferences across all your iOS devices effortlessly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-4 font-semibold">
                GET IN TOUCH
              </p>
              <h3 className="text-5xl font-serif font-bold text-white mb-8">
                Contact Information
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    ADDRESS
                  </p>
                  <p className="text-base text-white font-medium flex items-start">
                    <i className="ri-map-pin-line text-gray-400 mr-2 mt-1"></i>
                    Rua SAO JOSE 851
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    CITY
                  </p>
                  <p className="text-base text-white font-medium flex items-start">
                    <i className="ri-building-line text-gray-400 mr-2 mt-1"></i>
                    MACAPA
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    ZIP CODE
                  </p>
                  <p className="text-base text-white font-medium flex items-start">
                    <i className="ri-mail-line text-gray-400 mr-2 mt-1"></i>
                    68908-015
                  </p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    COUNTRY
                  </p>
                  <p className="text-base text-white font-medium flex items-start">
                    <i className="ri-global-line text-gray-400 mr-2 mt-1"></i>
                    Brazil
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    EMAIL
                  </p>
                  <p className="text-base text-white font-medium flex items-start">
                    <i className="ri-mail-send-line text-gray-400 mr-2 mt-1"></i>
                    help@fortselectltda.com
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    PHONE
                  </p>
                  <p className="text-base text-white font-medium flex items-start">
                    <i className="ri-phone-line text-gray-400 mr-2 mt-1"></i>
                    +1 8127753476
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 bg-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-gray-400">
            © 2025 FORT SELECT LTDA. All rights reserved. |{' '}
            <a 
              href="https://readdy.ai/?ref=logo" 
              className="text-gray-300 hover:text-white transition-colors whitespace-nowrap"
              target="_blank"
              rel="noopener noreferrer"
            >
              Website Builder
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
